import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface ContactDetail {
  id: string;
  type: string;
  value: string;
  label: string | null;
  sort_order: number;
}

export interface SeoSettings {
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  canonical_url: string;
  og_title: string;
  og_description: string;
  og_image: string;
  robots: string;
}

export interface AdsSettings {
  enabled: boolean;
  conversion_id: string;
  script: string;
}

export interface SiteSettings {
  marquee: { text: string; enabled: boolean };
  language: { show_switch: boolean };
  pricing: { show_prices: boolean };
  site: { site_name: string; tagline: string; copyright: string; footer_text: string };
  notification: { email: string; from_email: string };
  social: { facebook: string; instagram: string; twitter: string; whatsapp: string; whatsapp_enabled: boolean };
  contactDisplay: {
    phone_footer: boolean;
    phone_contact: boolean;
    phone_part_request: boolean;
    phone_contact_cta: boolean;
    email_footer: boolean;
    email_contact: boolean;
    address_footer: boolean;
    address_contact: boolean;
  };
  seo: SeoSettings;
  ads: AdsSettings;
  contactDetails: ContactDetail[];
}

const DEFAULTS: SiteSettings = {
  marquee: { text: "", enabled: true },
  language: { show_switch: true },
  pricing: { show_prices: true },
  site: {
    site_name: "Top Gear Auto Spares",
    tagline: "",
    copyright: "© Top Gear Auto Spares. All Rights Reserved.",
    footer_text: "",
  },
  notification: { email: "", from_email: "sales@topgearautospares.com" },
  social: { facebook: "", instagram: "", twitter: "", whatsapp: "", whatsapp_enabled: true },
  contactDisplay: {
    phone_footer: true,
    phone_contact: true,
    phone_part_request: true,
    phone_contact_cta: true,
    email_footer: true,
    email_contact: true,
    address_footer: true,
    address_contact: true,
  },
  seo: {
    meta_title: "Top Gear Auto Spares | Used Engines, Transmissions & Parts",
    meta_description: "Tested used engines, transmissions and body parts from Top Gear Auto Spares, Darwin. Quality second-hand car parts shipped Australia-wide with warranty.",
    meta_keywords: "used engines, used transmissions, auto body parts, second hand car parts, Darwin auto spares",
    canonical_url: "https://topgearautospares.com/",
    og_title: "Top Gear Auto Spares | Used Engines, Transmissions & Parts",
    og_description: "Tested used engines, transmissions and body parts from Top Gear Auto Spares, Darwin. Quality second-hand car parts shipped Australia-wide with warranty.",
    og_image: "",
    robots: "index, follow",
  },
  ads: { enabled: false, conversion_id: "", script: "" },
  contactDetails: [],
};

// Deduplicates concurrent in-flight fetches within the same render tick, but
// never caches the resolved value permanently — so every freshly mounted page
// (e.g. navigating from the admin dashboard to the public site) re-reads the
// latest settings from the database. This is what makes the price / marquee /
// language toggles take effect immediately.
let pending: Promise<SiteSettings> | null = null;

function fetchSettings(): Promise<SiteSettings> {
  if (pending) return pending;

  pending = (async () => {
    const settings: SiteSettings = {
      ...DEFAULTS,
      marquee: { ...DEFAULTS.marquee },
      language: { ...DEFAULTS.language },
      pricing: { ...DEFAULTS.pricing },
      site: { ...DEFAULTS.site },
      notification: { ...DEFAULTS.notification },
      social: { ...DEFAULTS.social },
      contactDisplay: { ...DEFAULTS.contactDisplay },
      seo: { ...DEFAULTS.seo },
      ads: { ...DEFAULTS.ads },
    };

    try {
      const { data: settingsData } = await supabase
        .from("site_settings")
        .select("key, value");

      (settingsData || []).forEach((row) => {
        const k = row.key as keyof Omit<SiteSettings, "contactDetails">;
        if (k in settings && row.value && typeof row.value === "object") {
          (settings as unknown as Record<string, unknown>)[k] = {
            ...(settings as unknown as Record<string, unknown>)[k],
            ...(row.value as Record<string, unknown>),
          };
        }
      });

      const { data: cd } = await supabase
        .from("contact_details")
        .select("*")
        .is("deleted_at", null)
        .order("sort_order", { ascending: true });

      settings.contactDetails = (cd as ContactDetail[]) || [];
    } catch {
      // keep defaults on failure
    }

    pending = null;
    return settings;
  })();

  return pending;
}

export function useSiteSettings(): SiteSettings {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULTS);

  useEffect(() => {
    let active = true;
    fetchSettings().then((s) => {
      if (active) setSettings(s);
    });
    return () => {
      active = false;
    };
  }, []);

  return settings;
}